import * as React from 'react';
import isFinite from 'lodash/isFinite';
import useFuzzySearch from '@packages/systems/ui/hooks/useFuzzySearch';
import useDebouncedValue from '@packages/systems/ui/hooks/useDebouncedValue';
import type {ValueProps} from '@packages/systems/dynamo/utils/ParamPanelNormalizers';
import {useCollectionItemsPage} from '@packages/systems/cms/client';
import {
  Autocomplete,
  AutocompleteItem,
  AutocompleteItemBody,
  InteractionsDefaultIcon,
  type AutocompleteOptions,
  type SearchableListProps,
  type ButtonPrefixSuffix,
} from '@packages/systems/spring';

// Disable filtering since we handle it via useFuzzySearch
const noopFilterOptions: SearchableListProps['filterOptions'] = ({options}) =>
  options;

type OptionType = {
  label: string;
  value: string;
  isDisabled?: boolean;
  description?: string;
  icon?: ButtonPrefixSuffix;
};

type Props = {
  availableItemRefOptions: ValueProps['availableItemRefOptions'];
  bindingSource: ValueProps['bindingSource'];
  itemRefOptionLabel: ValueProps['itemRefOptionLabel'];
  onValueChange: ValueProps['onValueChange'];
  stores: ValueProps['stores'];
  value: ValueProps['value'];
};

/**
 * Note: This is duplicated in public/js/designer-flux/components/ParamPanel/ui/AsyncItemReferenceInput.tsx
 * Any changes here should most likely be made also to that.
 */
const AsyncItemReferenceInput = (props: Props) => {
  const {
    availableItemRefOptions,
    bindingSource,
    itemRefOptionLabel,
    onValueChange,
    value,
    stores: {
      collection: {collections},
    },
  } = props;
  const [query, setQuery] = React.useState('');
  const debouncedQuery = useDebouncedValue({value: query, wait: 150});
  let referencedCollection = null;

  if (
    bindingSource &&
    (bindingSource.type === 'ItemRef' || bindingSource.type === 'ItemRefSet')
  ) {
    const {legacyReferencedCollectionId} = bindingSource.meta;

    referencedCollection = legacyReferencedCollectionId
      ? collections.get(legacyReferencedCollectionId)
      : null;
  }

  const {data: collectionItemsResponse = {}} = useCollectionItemsPage(
    referencedCollection,
    {
      format: 'itemRef',
      query: debouncedQuery,
    }
  );

  // @ts-expect-error - TS2339 - Property 'hasMore' does not exist on type '{}'.
  const hasMoreMatchingButUnloadedItems = collectionItemsResponse.hasMore;
  const numberOfMatchingButUnloadedItems =
    // @ts-expect-error - TS2339 - Property 'total' does not exist on type '{}'.
    isFinite(collectionItemsResponse.total) &&
    // @ts-expect-error - TS2339 - Property 'count' does not exist on type '{}'.
    isFinite(collectionItemsResponse.count)
      ? // @ts-expect-error - TS2339 - Property 'total' does not exist on type '{}'. | TS2339 - Property 'count' does not exist on type '{}'.
        collectionItemsResponse.total - collectionItemsResponse.count
      : 0;

  const searchableItems: Array<OptionType> = React.useMemo(() => {
    return availableItemRefOptions.map((itemRef) => ({
      label: itemRef.value === value ? itemRefOptionLabel : itemRef.label,
      value: itemRef.value || '',
      icon: itemRef.lightning ? <InteractionsDefaultIcon /> : undefined,
    }));
  }, [availableItemRefOptions, itemRefOptionLabel, value]);

  const [filteredItems] = useFuzzySearch({
    query: debouncedQuery,
    onQueryChange: setQuery,
    items: searchableItems,
    // @ts-expect-error - TS2322 - Type '(item: never) => never' is not assignable to type '(value: OptionType) => string'.
    getSearchableText: React.useCallback((item) => item.label, []),
    // @ts-expect-error - TS7006 - Parameter 'foundIndexes' implicitly has an 'any' type. | TS7006 - Parameter 'items' implicitly has an 'any' type. | TS7006 - Parameter 'mapItemAction' implicitly has an 'any' type.
    reduceItems: React.useCallback((foundIndexes, items, mapItemAction) => {
      // foundIndexes is over-eagerly tokenizing our searchable items, but we can't turn off `suggest`
      // because then we lose the nested field support.
      // to get around this, we can just make foundIndexes unique and it solves our problems.
      return Array.from(new Set(foundIndexes)).map((foundIndex) => ({
        // @ts-expect-error - TS2538 - Type 'unknown' cannot be used as an index type.
        match: items[foundIndex],
        // @ts-expect-error - TS2538 - Type 'unknown' cannot be used as an index type.
        actions: mapItemAction(items[foundIndex]),
      }));
    }, []),
  });

  const options: AutocompleteOptions = React.useMemo(() => {
    const opts: OptionType[] = filteredItems.map((option) => option.match);

    if (hasMoreMatchingButUnloadedItems) {
      opts.push({
        label: `And ${numberOfMatchingButUnloadedItems} more...`,
        value: '',
        isDisabled: true,
        description: 'Search for something more specific.',
      });
    }

    if (opts.length === 0 && query) {
      opts.push({
        label: 'No matching items found.',
        value: '',
        isDisabled: true,
        description: 'Search for something less specific.',
      });
    }

    if (availableItemRefOptions.length === 0) {
      opts.push({
        label: 'No items to select',
        value: '',
        isDisabled: true,
      });
    }

    return opts;
  }, [
    filteredItems,
    hasMoreMatchingButUnloadedItems,
    numberOfMatchingButUnloadedItems,
    query,
    availableItemRefOptions,
  ]);

  const placeholder = React.useMemo(() => {
    if (availableItemRefOptions.length === 0) {
      return 'No items to select';
    }
    return 'Search by item name';
  }, [availableItemRefOptions]);

  const handleChange = React.useCallback(
    (newValue: string | null) => {
      if (newValue !== null) {
        onValueChange(newValue);
        setQuery('');
      }
    },
    [onValueChange]
  );

  const renderOption = React.useCallback(
    (option: OptionType) => (
      <AutocompleteItem isDisabled={option.isDisabled} prefix={option.icon}>
        <AutocompleteItemBody description={option.description}>
          {option.label}
        </AutocompleteItemBody>
      </AutocompleteItem>
    ),
    []
  );

  return (
    <Autocomplete
      data-automation-id="filter-itemref-input"
      onInputChange={setQuery}
      onChange={handleChange}
      options={options}
      value={value || null}
      placeholder={placeholder}
      filterOptions={noopFilterOptions}
      isClearable={false}
      renderOption={renderOption}
    />
  );
};

export default AsyncItemReferenceInput;
